import { useTranslation } from 'react-i18next';
import { Box, Tooltip, Typography } from '@mui/material';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import useFetch from '../../../api/useFetch';
import ExoTable from '../../../components/exo/ExoTable';
import { ExoTableUser } from '../../../components/exo/ExoTableUser';
import MoneyLabel from '../../../components/label/MoneyLabel';
import DateLabel from '../../../components/label/DateLabel';
import { ExpandReimbursement } from './ExpandReimbursement';

/**
 * All travel expense reimbursements, read-only: the documents are generated
 * from the approved requests, so nothing is edited here — only bank
 * transactions can be assigned. Expanding a row shows the full reports.
 */
export const ReimbursementsTab = () => {
  const { t } = useTranslation();

  const { apiData, isLoading } = useFetch(
    'travel-expense-reimbursements?sortBy=startDate&sortOrder=desc',
  );

  const Columns = [
    {
      header: (
        <Tooltip title={t('Billed')}>
          <PriceCheckIcon />
        </Tooltip>
      ), // header of column
      key: 'openAmount', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      width: '33px', // width of column
      cell: ({ row }) => (
        <Box>{Number(row.openAmount) === 0 && <PriceCheckIcon />}</Box>
      ),
    },
    {
      header: (
        <Tooltip title={t('Document Present')}>
          <RequestPageIcon />
        </Tooltip>
      ), // header of column
      key: 'file', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      width: '33px', // width of column
      cell: ({ row }) => <Box>{row.fileId && <RequestPageIcon />}</Box>,
    },
    {
      header: t('Employee'), // header of column
      key: 'user', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => <ExoTableUser user={row.user} />, // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Period'), // header of column
      key: 'startDate', // key of column in row-object
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      sortable: true, // enable sort function for column
      width: '190px', // width of column
      hiddenMobile: true,
      cell: ({ row }) => (
        <Box className="monospace whitespace-nowrap">
          <DateLabel date={row.startDate} /> - <DateLabel date={row.endDate} />
        </Box>
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Trips'), // header of column
      key: 'tripCount', // key of column in row-object
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      width: '70px', // width of column
      hiddenMobile: true,
    },
    {
      header: t('Brutto'), // header of column
      key: 'amount', // key of column in row-object
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      sortable: true, // enable sort function for column
      width: '130px', // width of column
      cell: ({ row }) => (
        <Box className="monospace">
          <MoneyLabel money={row.amount} />
        </Box>
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Open'), // header of column
      key: 'openAmountValue', // key of column in row-object
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      width: '130px', // width of column
      hiddenMobile: true,
      cell: ({ row }) => (
        <Typography className="monospace">
          <b>
            <MoneyLabel money={row.openAmount} />
          </b>
        </Typography>
      ), // custom xml for cells in the column. you get the row as object
    },
  ];

  return (
    <ExoTable
      extendElement={({ row, onUpdate }) => (
        <ExpandReimbursement row={row} onUpdate={onUpdate} />
      )}
      data={apiData}
      isLoading={isLoading}
      columns={Columns}
      columnSettingsKey="travelExpenseReimbursements"
    />
  );
};
